import { useState, useEffect } from 'react';
import { X, Send, Phone, Mail, User, Shield, Briefcase, CheckCircle, ChevronLeft, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { userInvitationService } from '../utils/users/userInvitationService';
import { UserInviteRequest, UserRole } from '../types/userInvitation';
import { db } from '../utils/firebase/client';
import { doc, getDoc } from 'firebase/firestore';

interface UserInviteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    user: {
        id: string;
        name: string;
        email: string;
    };
    company: {
        id: string;
        name: string;
        companyId?: string;
    };
}

export default function UserInviteModal({
    isOpen,
    onClose,
    onSuccess,
    user,
    company,
}: UserInviteModalProps) {
    const [formData, setFormData] = useState<UserInviteRequest>({
        phoneNumber: '',
        role: 'pumper',
        name: '',
        email: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [resolvedCompanyName, setResolvedCompanyName] = useState<string>(company.name);

    useEffect(() => {
        const fetchCompanyName = async () => {
            if (!company.id || company.id === 'default') return;
            try {
                const companyDoc = await getDoc(doc(db, 'companies', company.id));
                if (companyDoc.exists()) {
                    const data = companyDoc.data();
                    if (data?.companyName) {
                        setResolvedCompanyName(data.companyName);
                    }
                }
            } catch (error) {
            }
        };

        if (isOpen) fetchCompanyName();
    }, [isOpen, company.id]);

    if (!isOpen) return null;

    const handleChange = (field: keyof UserInviteRequest, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const formatPhoneNumber = (value: string) => {
        const digits = value.replace(/\D/g, '');
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    const handlePhoneChange = (value: string) => {
        const formatted = formatPhoneNumber(value);
        handleChange('phoneNumber', formatted);
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        const phoneDigits = formData.phoneNumber.replace(/\D/g, '');

        if (!phoneDigits) errors.phoneNumber = 'Phone number is required';
        else if (phoneDigits.length !== 10) errors.phoneNumber = 'Phone number must be 10 digits';

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email address';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors');
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await userInvitationService.createInvitation(
                formData,
                { id: user.id, name: user.name },
                { id: company.id, name: resolvedCompanyName }
            );

            if (result.success) {
                toast.success(
                    <div className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                            <p className="font-medium">Invitation Sent!</p>
                            <p className="text-sm text-gray-600 mt-1">
                                SMS sent to {formData.phoneNumber}.
                            </p>
                        </div>
                    </div>
                );
                onSuccess();
                onClose();
                setFormData({ phoneNumber: '', role: 'pumper', name: '', email: '' });
            } else {
                toast.error(result.message || 'Failed to invite user');
            }
        } catch (error) {
            toast.error('Detailed error: ' + (error instanceof Error ? error.message : 'Unknown'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Standard accessible backdrop */}
            <div
                className="absolute inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
            />

            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-200 dark:border-slate-800 overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite Team Member</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Add a new user to {resolvedCompanyName}</p>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Role Selection */}
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => handleChange('role', 'pumper')}
                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${formData.role === 'pumper'
                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-800 bg-white dark:bg-slate-800'
                                }`}
                        >
                            <Briefcase className={`w-6 h-6 mb-2 ${formData.role === 'pumper' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                                }`} />
                            <span className={`font-semibold text-sm ${formData.role === 'pumper' ? 'text-blue-900 dark:text-blue-100' : 'text-gray-700 dark:text-gray-300'
                                }`}>Field Pumper</span>
                        </button>

                        <button
                            onClick={() => handleChange('role', 'manager')}
                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${formData.role === 'manager'
                                ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                                : 'border-gray-200 dark:border-slate-700 hover:border-purple-200 dark:hover:border-purple-800 bg-white dark:bg-slate-800'
                                }`}
                        >
                            <Shield className={`w-6 h-6 mb-2 ${formData.role === 'manager' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'
                                }`} />
                            <span className={`font-semibold text-sm ${formData.role === 'manager' ? 'text-purple-900 dark:text-purple-100' : 'text-gray-700 dark:text-gray-300'
                                }`}>Manager</span>
                        </button>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                        {/* Phone - Primary */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Mobile Phone <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-4 w-4 text-gray-400" />
                                </div>
                                <Input
                                    value={formData.phoneNumber}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                    placeholder="(555) 000-0000"
                                    className="pl-9 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                                    autoFocus
                                />
                            </div>
                            {validationErrors.phoneNumber && (
                                <p className="text-red-600 text-xs flex items-center gap-1 mt-1">
                                    <AlertCircle className="w-3 h-3" /> {validationErrors.phoneNumber}
                                </p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                We'll send an invite link via SMS to this number.
                            </p>
                        </div>

                        {/* Name & Email - Optional Row */}
                        <div className="space-y-4 pt-2">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Name <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="Full Name"
                                        className="pl-9 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <Input
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="user@example.com"
                                        className="pl-9 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-950 border-t border-gray-100 dark:border-slate-800 flex items-center justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Sending...</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span>Send Invitation</span>
                                <Send className="w-4 h-4" />
                            </div>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
